import { memo, useEffect } from "react"
import { brackyArenaConfig } from "./data/bracky-arena-config"
import { useBrackyAnimation } from "./hooks/use-bracky-animation"
import LandingCountUp from "./LandingCountUp"
import { LandingLiveDotLarge } from "./LandingLiveDotLarge"
import { LandinguseBrackyPrice } from "./LandinguseBrackyPrice"

const ScanlinesOverlay = memo(() => (
  <div className="pointer-events-none absolute inset-0 scanlines rounded-xl opacity-20 mix-blend-multiply z-100" />
))

interface BrackyArenaScoreboardProps {
  fanCount?: string
}

export const BrackyArenaScoreboard = memo(function BrackyArenaScoreboard({
  fanCount = "72K+",
}: BrackyArenaScoreboardProps) {
  const { shouldAnimate, setShouldAnimate, sectionRef, prevValuesRef } = useBrackyAnimation()
  const { data: brackyData } = LandinguseBrackyPrice()
  const { stats, classes, gradients } = brackyArenaConfig

  // Check for value changes in dynamic data
  useEffect(() => {
    if (!brackyData?.holders) return

    const currentHolders = brackyData.holders
    const prevHolders = prevValuesRef.current["Global Fan Network"]

    // If we have a previous value and it's different from current, trigger animation
    if (prevHolders !== undefined && prevHolders !== currentHolders && currentHolders > 0) {
      setShouldAnimate(true)
    }

    // Update previous values
    prevValuesRef.current["Global Fan Network"] = currentHolders
  }, [brackyData?.holders, setShouldAnimate, prevValuesRef])

  return (
    <section ref={sectionRef}>
      {/* Heading */}
      <h2 className="text-white tracking-tight font-extrabold text-center mb-4 leading-tight text-h3">
        Enter Bracky's Arena
      </h2>

      {/* Scoreboard Container */}
      <div className={classes.scoreboard}>
        <div className="relative z-10 flex flex-col lg:flex-row text-center">
          {stats.map(({ numericValue, suffix, label }) => {
            const liveHolders = brackyData?.holders && brackyData.holders > 0 ? brackyData.holders : numericValue
            const finalValue = label === "Global Fan Network" ? liveHolders : numericValue
            const showLiveDot = label === "$BRACKY Holders" || label === "Global Fan Network"

            return (
              <div key={`stat-${label}`} className={classes.statContainer}>
                {/* Label */}
                <div className={classes.statLabel}>{label}</div>
                {/* Value */}
                <div
                  className={`${classes.statValue} ${showLiveDot ? "flex items-center justify-center gap-1" : ""}`}
                  style={{ position: "relative" }}
                >
                  {/* Scanlines overlay */}
                  <ScanlinesOverlay />
                  {/* Top-fade overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none rounded-t-xl"
                    style={{ background: gradients.topFade }}
                  />
                  {showLiveDot && (
                    <LandingLiveDotLarge className="scale-[.85] sm:scale-[.88] lg:scale-[.99] mr-[-.22rem] sm:mr-[-.15rem] lg:mr-[-0.05rem] translate-y-[.05rem]" />
                  )}
                  <LandingCountUp
                    end={finalValue}
                    start={shouldAnimate ? 0 : finalValue}
                    suffix={suffix}
                    isActive={shouldAnimate}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Kicker below scoreboard */}
      <div className="flex justify-center mb-[0.2rem] sm:mb-[1.1rem] mt-4 sm:mt-7">
        <div className="relative inline-block">
          <div className="relative z-10 pl-[.8rem] pr-4 -mb-[1.6rem] sm:-mb-8 sm:-mt-2 flex items-center gap-1.5 sm:whitespace-nowrap">
            <span className="text-[.875rem] sm:text-[1.04rem] font-normal tracking-wider text-center">
              <span className="sm:hidden text-white/70 uppercase">
                JOIN <span className="text-[#22FF65] font-semibold">{fanCount}</span> ALREADY BETTING LIVE <br /> IN THE
                BRACKET ARENA
              </span>
              <span className="hidden sm:inline lg:hidden text-white/70 uppercase">
                JOIN <span className="text-[#22FF65] font-semibold">{fanCount}</span> ALREADY BETTING LIVE WITH BRACKY
                <br />
                IN THE BRACKET ARENA
              </span>
              <span className="hidden lg:inline text-white/70 uppercase">
                JOIN <span className="text-[#22FF65] font-semibold">{fanCount}</span> ALREADY BETTING LIVE WITH BRACKY
                IN THE BRACKET ARENA
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  )
})
